<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useSession } from '../auth';
import { useCardDesigner } from '../composables/useCardDesigner';
import AppLayout from '@/components/layout/AppLayout.vue';
import { Button } from '@/components/ui/button';
import { marked } from 'marked';

marked.setOptions({ async: false });

function normalizeMarkdown(text: string): string {
  return text
    .replace(/(?<!\n)\n(#{1,6} )/g, '\n\n$1')
    .replace(/(?<!\n)\n(---+|\*\*\*+|___+)\s*$/gm, '\n\n$1')
    .replace(/([^\n])\n([-*] |\d+\. )/g, '$1\n\n$2');
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const { messages, isStreaming, error, createdCards, sendMessage, reset } = useCardDesigner();

const sessionId = ref('');
const setupError = ref('');
const dataLoading = ref(true);

const inputText = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function toolLabel(name: string, status: string): string {
  const running = status === 'running';
  switch (name) {
    case 'create_card':
      return running ? 'Saving card…' : 'Card saved';
    case 'create_deck':
      return running ? 'Creating deck…' : 'Deck created';
    case 'list_decks':
      return running ? 'Checking decks…' : 'Checked decks';
    default:
      return running ? 'Working…' : 'Done';
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function autoResize(event: Event) {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
  scrollToBottom();
}

async function handleSubmit() {
  if (!inputText.value.trim() || isStreaming.value || !sessionId.value) return;
  const text = inputText.value;
  inputText.value = '';
  if (textareaRef.value) textareaRef.value.style.height = 'auto';
  scrollToBottom();
  await sendMessage(sessionId.value, text);
  scrollToBottom();
}

watch(
  () => messages.value.map((m) => m.content).join(''),
  () => scrollToBottom()
);

onMounted(async () => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }

  reset();

  try {
    const res = await fetch(`${API_URL}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: 'card_designer' }),
    });
    if (!res.ok) throw new Error('Could not start a new card session');
    const data = (await res.json()) as { id: string };
    sessionId.value = data.id;
  } catch (e) {
    setupError.value = e instanceof Error ? e.message : 'Failed to start the card designer';
    dataLoading.value = false;
    return;
  }

  dataLoading.value = false;

  // Open the conversation: no user message → the assistant greets and asks what to capture.
  await sendMessage(sessionId.value);
  scrollToBottom();
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <div class="px-3 pt-2 pb-3">
        <button
          class="text-xs text-muted-foreground hover:text-foreground"
          @click="router.push('/decks')"
        >
          &larr; Back to Decks
        </button>
      </div>

      <div class="px-3 pb-2">
        <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Created ({{ createdCards.length }})
        </h3>
        <div
          v-if="createdCards.length === 0"
          class="text-xs text-muted-foreground italic"
        >
          None yet — cards you save will appear here.
        </div>
        <ul
          v-else
          class="space-y-2"
        >
          <li
            v-for="card in createdCards"
            :key="card.shortId"
            class="text-xs text-muted-foreground leading-snug"
          >
            <span class="text-foreground">{{ card.question }}</span>
            <span class="block text-[10px] uppercase tracking-wide text-muted-foreground/70 mt-0.5">
              {{ card.deckName }}
            </span>
          </li>
        </ul>
      </div>
    </template>

    <template #mobile-top>
      <div class="md:hidden px-4 py-2 flex items-center justify-between shrink-0 border-b border-border">
        <button
          class="text-sm text-muted-foreground hover:text-foreground"
          @click="router.push('/decks')"
        >
          &larr; Back
        </button>
        <span class="text-sm font-medium truncate ml-3">
          New card
        </span>
      </div>
    </template>

    <div
      v-if="dataLoading"
      class="flex-1 flex items-center justify-center text-muted-foreground"
    >
      Loading...
    </div>

    <div
      v-else-if="setupError"
      class="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6"
    >
      <div class="bg-destructive/10 text-destructive p-4 rounded-md max-w-md">
        {{ setupError }}
      </div>
      <Button
        variant="ghost"
        @click="router.push('/decks')"
      >
        Back to Decks
      </Button>
    </div>

    <template v-else>
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto scrollbar-thin"
      >
        <div class="max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
          <div
            v-for="(message, msgIdx) in messages"
            :key="msgIdx"
            class="flex"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              :class="
                message.role === 'user'
                  ? 'max-w-[80%] rounded-2xl px-4 py-2 bg-primary text-primary-foreground'
                  : 'w-full text-foreground'
              "
            >
              <div
                v-if="message.role === 'assistant'"
                class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                v-html="marked.parse(normalizeMarkdown(message.content.trim()))"
              />
              <div
                v-else
                class="whitespace-pre-wrap"
              >
                {{ message.content.trim() }}
              </div>
              <span
                v-if="message.isStreaming && !message.content"
                class="inline-flex gap-1"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
              </span>
              <span
                v-else-if="message.isStreaming"
                class="inline-block w-0.5 h-4 bg-current animate-pulse ml-0.5 align-text-bottom"
              />
              <div
                v-if="message.role === 'assistant' && message.toolUses?.length"
                class="mt-2 flex flex-wrap gap-1.5"
              >
                <div
                  v-for="tu in message.toolUses"
                  :key="tu.id"
                  class="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-border bg-background/60"
                  :class="tu.status === 'error' ? 'text-destructive' : 'text-muted-foreground'"
                >
                  <span
                    v-if="tu.status === 'running'"
                    class="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                  />
                  <span
                    v-else-if="tu.status === 'done'"
                    class="text-green-600 dark:text-green-400"
                  >&#10003;</span>
                  <span v-else>&#9888;</span>
                  {{ toolLabel(tu.name, tu.status) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="shrink-0 px-6 pb-4">
        <div class="max-w-3xl mx-auto w-full">
          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-3 rounded-md mb-3 text-sm"
          >
            {{ error }}
          </div>

          <div class="pb-2 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              :disabled="isStreaming"
              @click="router.push('/decks')"
            >
              Done — back to decks
            </Button>
          </div>

          <form
            class="flex items-end gap-2"
            @submit.prevent="handleSubmit"
          >
            <textarea
              ref="textareaRef"
              v-model="inputText"
              :disabled="isStreaming"
              rows="1"
              class="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none max-h-40 overflow-y-auto scrollbar-thin"
              placeholder="e.g., I want to remember the word euphemism for its definition…"
              @keydown.enter.exact.prevent="handleSubmit"
              @input="autoResize"
            />
            <Button
              type="submit"
              :disabled="isStreaming || !inputText.trim()"
            >
              Send
            </Button>
          </form>
        </div>
      </div>
    </template>
  </AppLayout>
</template>
